import React, { useEffect, useState, useRef } from "react";
import { Bell, MoreHorizontal, LogOut, KeyRound } from "lucide-react";
import "./AdminHome.css";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  ReferenceDot,
} from "recharts";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || "";

const COLORS = ["#7C3AED", "#EF4444", "#F59E0B", "#10B981", "#06B6D4"];

function fmtCurrency(val) {
  if (val == null) return "—";
  const n = Number(val) || 0;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function formatDateISO(d) {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function lastNDates(n) {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(formatDateISO(d));
  }
  return out;
}

// ---------- Notifications: use NotificationsPage logic ----------
/**
 * Try to determine current user's id from localStorage.userId or JWT token payload
 */
function getUserIdFromStorageOrToken() {
  const ls = localStorage.getItem("userId");
  if (ls) {
    const n = Number(ls);
    if (!Number.isNaN(n)) return n;
  }
  const token = localStorage.getItem("token");
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payloadB64.length % 4;
    const padded = payloadB64 + (pad ? "=".repeat(4 - pad) : "");
    const json = atob(padded);
    const payload = JSON.parse(json);
    if (payload.sub && !Number.isNaN(Number(payload.sub)))
      return Number(payload.sub);
    if (payload.id && !Number.isNaN(Number(payload.id)))
      return Number(payload.id);
    if (payload.userId && !Number.isNaN(Number(payload.userId)))
      return Number(payload.userId);
    if (payload.user && (payload.user.id || payload.user.userId)) {
      const maybe = payload.user.id ?? payload.user.userId;
      if (!Number.isNaN(Number(maybe))) return Number(maybe);
    }
  } catch (e) {
    return null;
  }
  return null;
}

export default function AdminHome() {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const popupRef = useRef(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");

  const [appsPieData, setAppsPieData] = useState([]);
  const [payrollChartData, setPayrollChartData] = useState([]);
  const [incomeChartData, setIncomeChartData] = useState([]);
  const [incomeAnnotation, setIncomeAnnotation] = useState(null);

  // NEW: emails state
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState("");

  const [showChangePwd, setShowChangePwd] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState(null);
  const [pwdLoading, setPwdLoading] = useState(false);

  // ---------- Notifications integration ----------
  const derivedUserId = getUserIdFromStorageOrToken();
  const notifMountedRef = useRef(true);

  async function loadNotifications() {
    if (!derivedUserId) {
      console.warn(
        "[notifications] no user id available (localStorage.userId or token payload)"
      );
      setError(
        "No user id found for notifications. Set localStorage.userId or ensure token contains sub/id/userId."
      );
      return;
    }

    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/users-notifications/user/${derivedUserId}`,
        {
          headers: apiHeaders(false),
        }
      );

      const ct = res.headers.get("content-type") || "";
      const body = ct.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text().catch(() => null);

      if (!res.ok) {
        throw new Error(
          body?.error || body || `Failed to load notifications (${res.status})`
        );
      }

      const arr = Array.isArray(body)
        ? body
        : body?.rows ?? body?.data ?? (body ? [body] : []);
      const normalized = (arr || []).map((n) => ({
        id: n.id ?? n._id ?? null,
        message: n.message ?? n.text ?? n.msg ?? "",
        is_read:
          typeof n.is_read === "boolean"
            ? n.is_read
            : n.read || n.isRead || false,
        created_at:
          n.created_at ?? n.createdAt ?? n.date ?? n.timestamp ?? null,
        raw: n,
      }));

      if (notifMountedRef.current) setNotifications(normalized);
      console.info("[notifications] loaded", normalized.length);
    } catch (err) {
      console.error("loadNotifications error:", err);
      setError(err.message || "Failed to load notifications");
    }
  }

  async function markRead(id) {
    if (!id) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/users-notifications/${id}/read`,
        {
          method: "PUT",
          headers: apiHeaders(false),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.warn("markRead server returned:", res.status, txt);
      }
    } catch (err) {
      console.error("markRead error:", err);
    } finally {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    }
  }

  async function markAllRead() {
    if (!derivedUserId) {
      setError("No userId available to mark notifications as read.");
      return;
    }
    if (notifications.length === 0) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/users-notifications/user/${derivedUserId}/read-all`,
        {
          method: "PUT",
          headers: apiHeaders(false),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.warn("markAllRead server returned:", res.status, txt);
      }
    } catch (err) {
      console.error("markAllRead error:", err);
    } finally {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    }
  }

  useEffect(() => {
    notifMountedRef.current = true;
    if (derivedUserId) loadNotifications();
    else
      console.debug(
        "[notifications] derived user id is null; waiting for token or localStorage.userId"
      );
    try {
      window.__debug_set_notifications = (arr) => {
        if (!notifMountedRef.current) return;
        setNotifications(Array.isArray(arr) ? arr : []);
        console.info(
          "[notifications] debug: manually set",
          Array.isArray(arr) ? arr.length : 0
        );
      };
    } catch (e) {}
    return () => {
      notifMountedRef.current = false;
    };
  }, [derivedUserId]);

  // ---------- Password change handler ----------
  async function handlePasswordChange(e) {
    e && e.preventDefault();
    setPwdMsg(null);

    if (!oldPwd || !newPwd || !confirmPwd) {
      setPwdMsg({ type: "error", text: "Please fill all fields." });
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: "error", text: "New passwords do not match." });
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg({
        type: "error",
        text: "Password must be at least 6 characters.",
      });
      return;
    }

    try {
      setPwdLoading(true);
      const res = await fetch(`${API_BASE}/api/users/change-password`, {
        method: "POST",
        headers: apiHeaders(true),
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd }),
      });

      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        setPwdMsg({
          type: "error",
          text: payload?.error || payload?.message || "Failed",
        });
        return;
      }

      setPwdMsg({ type: "success", text: "Password changed successfully." });
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setTimeout(() => {
        setShowChangePwd(false);
        setPwdMsg(null);
      }, 1000);
    } catch (err) {
      console.error(err);
      setPwdMsg({ type: "error", text: "Network error" });
    } finally {
      setPwdLoading(false);
    }
  }

  // Fetch current logged-in user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: apiHeaders(false),
        });
        if (!res.ok) throw new Error("Failed to fetch user");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  // Fetch core data
  useEffect(() => {
    let mounted = true;
    async function loadCore() {
      setLoading(true);
      setError("");
      try {
        const [projRes, clientsRes, empRes, payRes, usersRes] =
          await Promise.all([
            fetch(`${API_BASE}/api/projects`, { headers: apiHeaders(false) }),
            fetch(`${API_BASE}/api/clients`, { headers: apiHeaders(false) }),
            fetch(`${API_BASE}/api/employees`, { headers: apiHeaders(false) }),
            fetch(`${API_BASE}/api/payments`, { headers: apiHeaders(false) }),
            fetch(`${API_BASE}/api/users`, { headers: apiHeaders(false) }),
          ]);

        const projBody = projRes.ok ? await projRes.json().catch(() => []) : [];
        const clientsBody = clientsRes.ok
          ? await clientsRes.json().catch(() => [])
          : [];
        const empBody = empRes.ok ? await empRes.json().catch(() => []) : [];
        const payBody = payRes.ok ? await payRes.json().catch(() => []) : [];
        const usersBody = usersRes.ok
          ? await usersRes.json().catch(() => [])
          : [];

        if (!mounted) return;

        setProjects(projBody);
        setClients(clientsBody);
        setEmployees(empBody);
        setPayments(payBody);
        setUsers(usersBody);
      } catch (err) {
        console.error("AdminHome core load error:", err);
        if (mounted) setError("Failed to load core data");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadCore();
    return () => {
      mounted = false;
    };
  }, []);

  // Compute stats
  useEffect(() => {
    if (
      !Array.isArray(payments) ||
      !Array.isArray(users) ||
      !Array.isArray(employees)
    )
      return;
    setStatsLoading(true);
    try {
      const roleMap = new Map();
      for (const u of users) {
        const r = (u.role || "unknown").toString();
        roleMap.set(r, (roleMap.get(r) || 0) + 1);
      }
      const pieEntries = Array.from(roleMap.entries()).map(([name, value]) => ({
        name,
        value,
      }));
      const employeesCount = employees.length;
      if (employeesCount > 0) {
        pieEntries.push({ name: "Employees", value: employeesCount });
      }
      const pie = pieEntries.map((e, i) => ({
        ...e,
        fill: COLORS[i % COLORS.length],
      }));
      setAppsPieData(pie);

      const DAYS = 10;
      const dayLabels = lastNDates(DAYS);
      const byDate = {};
      for (const d of dayLabels) byDate[d] = { label: d };

      const categoryTotals = {};
      for (const p of payments) {
        const cat = (p.category || p.method || "other").toString();
        categoryTotals[cat] =
          (categoryTotals[cat] || 0) + Number(p.amount || 0);
      }

      const sortedCats = Object.entries(categoryTotals)
        .sort((a, b) => b[1] - a[1])
        .map((x) => x[0]);
      const stackCats = sortedCats.slice(0, 3);
      if (stackCats.length === 0) stackCats.push("other");

      for (const p of payments) {
        const dateRaw = p.payment_date || p.updated_at || p.created_at;
        if (!dateRaw) continue;
        const dt = formatDateISO(dateRaw);
        if (!(dt in byDate)) continue;
        const cat = stackCats.includes(p.category)
          ? p.category
          : stackCats[0] || "other";
        byDate[dt][cat] = (byDate[dt][cat] || 0) + Number(p.amount || 0);
      }

      const payrollData = dayLabels.map((d) => {
        const row = byDate[d] || { label: d };
        for (const c of stackCats) row[c] = Number(row[c] || 0);
        return row;
      });
      setPayrollChartData(payrollData);

      const DAYS_INCOME = 14;
      const incomeLabels = lastNDates(DAYS_INCOME);
      const incomeByDate = {};
      for (const L of incomeLabels) incomeByDate[L] = 0;
      for (const p of payments) {
        if ((p.type || "").toLowerCase() !== "income") continue;
        const dateRaw = p.payment_date || p.updated_at || p.created_at;
        if (!dateRaw) continue;
        const dt = formatDateISO(dateRaw);
        if (!(dt in incomeByDate)) continue;
        incomeByDate[dt] += Number(p.amount || 0);
      }
      const incomeData = incomeLabels.map((d) => ({
        label: d,
        value: Number(incomeByDate[d] || 0),
      }));
      setIncomeChartData(incomeData);
      const maxPoint = incomeData.reduce(
        (acc, cur) => (cur.value > (acc.value || 0) ? cur : acc),
        {}
      );
      setIncomeAnnotation(maxPoint && maxPoint.value > 0 ? maxPoint : null);
    } catch (err) {
      console.error("stats compute error:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [payments, users, employees]);

  // --- NEW: fetch emails (bottom section) ---
  useEffect(() => {
    let mounted = true;
    const tryFetchEmails = async () => {
      setEmailsLoading(true);
      setEmailsError("");
      try {
        const token = localStorage.getItem("token");
        const urls = [
          `${API_BASE}/api/emails`,
          `${API_BASE}/api/mail`,
          `${API_BASE}/api/messages`,
          `${API_BASE}/emails`,
          `${API_BASE}/mail`,
        ];

        let lastErr = null;
        for (const url of urls) {
          if (!url) continue;
          try {
            const res = await fetch(url, {
              method: "GET",
              headers: token ? apiHeaders(false) : {},
              credentials: "include",
            });

            if (!res.ok) {
              lastErr = new Error(`HTTP ${res.status} at ${url}`);
              if (res.status === 404) continue;
              continue;
            }

            const body = await res.json().catch(() => null);
            let arr = Array.isArray(body)
              ? body
              : body?.rows ?? body?.data ?? (body ? [body] : []);
            if (!Array.isArray(arr)) arr = [];

            const normalized = arr.slice(0, 12).map((m) => ({
              id: m.id ?? m._id ?? null,
              from:
                m.from ??
                m.sender ??
                m.from_email ??
                m.fromAddress ??
                m.from_name ??
                "",
              subject: m.subject ?? m.title ?? "(no subject)",
              date:
                m.date ?? m.created_at ?? m.received_at ?? m.timestamp ?? null,
              snippet: m.snippet ?? m.preview ?? m.body?.slice?.(0, 140) ?? "",
              raw: m,
            }));

            if (!mounted) return;
            setEmails(normalized);
            setEmailsLoading(false);
            return;
          } catch (err) {
            lastErr = err;
            continue;
          }
        }
        throw lastErr || new Error("No email endpoint succeeded");
      } catch (err) {
        console.error("emails fetch error:", err);
        if (mounted) {
          setEmailsError("Failed to load emails (see console)");
          setEmails([]);
          setEmailsLoading(false);
        }
      }
    };

    tryFetchEmails();
    return () => {
      mounted = false;
    };
    // only run on mount
  }, []);

  const totalProjects = projects.length;
  const totalClients = clients.length;
  const totalEmployees = employees.length;
  const totalReceived = projects.reduce(
    (acc, p) => acc + Number(p.total_received || 0),
    0
  );
  const totalRemaining = projects.reduce((acc, p) => {
    const budget = Number(p.budget ?? 0);
    const received = Number(p.total_received ?? 0);
    return acc + (budget > 0 ? Math.max(0, budget - received) : 0);
  }, 0);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="dash-app-full">
      <main className="main-full">
        <header className="topbar">
          <div className="topbar-left">
            <div className="welcome">
              <h1>
                {loading ? "Loading..." : `Welcome ${user?.name || "Admin"}!`}
              </h1>
              <p className="muted">
                Today is {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Notification system */}
          <div className="topbar-right">
            <div className="controls">

              <div className="icon-btn bell-container" ref={popupRef}>
                <Bell
                  size={18}
                  onClick={() => {
                    setShowNotifications((s) => {
                      const next = !s;
                      if (!next) return next;

                      // Load newest notifications on open
                      loadNotifications();
                      return next;
                    });
                  }}
                  style={{ cursor: "pointer" }}
                />
                {/* Change Password Icon */}
              <KeyRound data-tooltip="Change Password"
                size={22}
                strokeWidth={2}
                className="topbar-icon"
                onClick={() => setShowChangePwd(true)}
                style={{
                  cursor: "pointer",
                  marginLeft: "12px",
                  color: "#7c3aed",
                  transition: "0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "#6d28d9")}
                onMouseOut={(e) => (e.currentTarget.style.color = "#7c3aed")}
              />

              {/* Logout Icon */}
              <LogOut data-tooltip="Logout"
                size={22}
                strokeWidth={2}
                className="topbar-icon"
                onClick={handleLogout}
                style={{
                  cursor: "pointer",
                  marginLeft: "12px",
                  color: "#ef4444",
                  transition: "0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "#dc2626")}
                onMouseOut={(e) => (e.currentTarget.style.color = "#ef4444")}
              />

                {/* Badge now shows ONLY if unreadCount > 0 */}
                {unreadCount > 0 && (
                  <span className="notif-badge">{unreadCount}</span>
                )}

                {showNotifications && (
                  <div className="notif-popup">
                    <div className="notif-header">
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                        }}
                      >
                        <strong>Notifications</strong>
                        <span style={{ color: "#6b7280", fontSize: 12 }}>
                          {unreadCount} unread
                        </span>
                      </div>
                    </div>

                    <div className="notif-list">
                      {notifications.length > 0 ? (
                        notifications.map((n, i) => (
                          <div
                            key={n.id ?? i}
                            className={`notif-item ${
                              n.is_read ? "read" : "unread"
                            }`}
                            onClick={() => {
                              if (!n.is_read) markRead(n.id);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="notif-text">
                              {n.message || n.text || "(no message)"}
                            </div>

                            <div className="notif-time">
                              {new Date(
                                n.created_at ||
                                  n.createdAt ||
                                  n.date ||
                                  Date.now()
                              ).toLocaleString()}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="notif-empty">No new notifications</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <section className="kpi-row">
          <div className="kpi small">
            <div className="kpi-title">Total number of staff</div>
            <div className="kpi-value">{totalEmployees}</div>
            <div className="kpi-note">From employees table</div>
          </div>
          <div className="kpi small">
            <div className="kpi-title">Total Projects</div>
            <div className="kpi-value">{totalProjects}</div>
            <div className="kpi-note">All projects in the system</div>
          </div>
          <div className="kpi small">
            <div className="kpi-title">Total Received</div>
            <div className="kpi-value">{fmtCurrency(totalReceived)}</div>
            <div className="kpi-note">Sum of verified income</div>
          </div>
          <div className="kpi small">
            <div className="kpi-title">Remaining to Receive</div>
            <div className="kpi-value">{fmtCurrency(totalRemaining)}</div>
            <div className="kpi-note">Budget - received</div>
          </div>
        </section>

        <section className="main-cards">
          <div className="grid-left">
            <div className="card kpi-card">
              <div className="card-head">
                <div>Staff distribution by role</div>
                <MoreHorizontal size={16} />
              </div>
              <div className="card-inner">
                {statsLoading ? (
                  <div className="chart-loading">Loading…</div>
                ) : appsPieData.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={appsPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={88}
                        paddingAngle={6}
                        startAngle={90}
                        endAngle={-270}
                      >
                        {appsPieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill || COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <ReTooltip formatter={(v) => String(v)} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        height={36}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-empty">No data</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid-mid">
            <div className="card chart-card">
              <div className="card-head">
                <div>Payroll by date (stacked)</div>
                <MoreHorizontal size={16} />
              </div>
              <div className="card-inner">
                {statsLoading ? (
                  <div className="chart-loading">Loading…</div>
                ) : payrollChartData.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={payrollChartData}
                      margin={{ top: 8, right: 6, left: 6, bottom: 6 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        tickFormatter={(v) =>
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                        }
                      />
                      <ReTooltip formatter={(v) => fmtCurrency(v)} />
                      <Legend verticalAlign="bottom" height={36} />
                      {(() => {
                        const keys = Object.keys(
                          payrollChartData[0] || {}
                        ).filter((k) => k !== "label");
                        return keys.map((k, i) => (
                          <Bar
                            key={k}
                            dataKey={k}
                            stackId="a"
                            fill={COLORS[i % COLORS.length]}
                          />
                        ));
                      })()}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-empty">No data</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid-right">
            <div className="card chart-card">
              <div className="card-head">
                <div>Total income</div>
                <MoreHorizontal size={16} />
              </div>
              <div className="card-inner">
                {statsLoading ? (
                  <div className="chart-loading">Loading…</div>
                ) : incomeChartData.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart
                      data={incomeChartData}
                      margin={{ top: 8, right: 6, left: 6, bottom: 6 }}
                    >
                      <defs>
                        <linearGradient
                          id="areaGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#7c3aed"
                            stopOpacity={0.22}
                          />
                          <stop
                            offset="100%"
                            stopColor="#7c3aed"
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        tickFormatter={(v) =>
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                        }
                      />
                      <ReTooltip formatter={(v) => fmtCurrency(v)} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#7c3aed"
                        fill="url(#areaGrad)"
                        strokeWidth={2}
                      />
                      {incomeAnnotation &&
                        (() => {
                          const ann = incomeAnnotation;
                          return (
                            <ReferenceDot
                              x={ann.date}
                              y={ann.value}
                              r={6}
                              fill="#ef4444"
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          );
                        })()}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-empty">No data</div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bottom-row">
          <div className="card table-card">
            <div className="card-head">
              <div>Payment vouchers</div>
              <MoreHorizontal size={16} />
            </div>
            <div className="table-body">
              <table className="payment-tt">
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Subject</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.slice(0, 6).map((p, idx) => (
                    <tr key={p.id || idx}>
                      <td>{String(idx + 1).padStart(2, "0")}</td>
                      <td>{p.notes || p.category || p.method || "Payment"}</td>
                      <td>
                        {p.payment_date
                          ? new Date(p.payment_date).toLocaleDateString()
                          : p.updated_at
                          ? new Date(p.updated_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td
                        className={`status ${
                          p.status === "paid" ? "approved" : "pending"
                        }`}
                      >
                        {p.status || "pending"}
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#64748b",
                        }}
                      >
                        No payments
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card table-card">
            <div className="card-head">
              <div>Budget history</div>
              <MoreHorizontal size={16} />
            </div>
            <div className="table-body">
              <table>
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Project</th>
                    <th>Budget</th>
                    <th>Received</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.slice(0, 6).map((p, idx) => (
                    <tr key={p.id || idx}>
                      <td>{String(idx + 1).padStart(2, "0")}</td>
                      <td>{p.name}</td>
                      <td>{fmtCurrency(p.budget)}</td>
                      <td>{fmtCurrency(p.total_received)}</td>
                      <td>
                        {p.updated_at
                          ? new Date(p.updated_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#64748b",
                        }}
                      >
                        No projects
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* NEW: Emails section (below bottom-row) */}
        <section className="emails-section">
          <div className="card table-card">
            <div className="card-head">
              <div>Recent Emails</div>
              <MoreHorizontal size={16} />
            </div>

            {/* force no scrollbar here (inline style) to avoid the "weird" scrollbar */}
            <div
              className="table-body emails-body"
              style={{ overflow: "visible", maxHeight: "none" }}
            >
              {emailsLoading ? (
                <div style={{ padding: 18 }}>Loading emails…</div>
              ) : emailsError ? (
                <div style={{ padding: 18, color: "#b91c1c" }}>
                  {emailsError}
                </div>
              ) : emails.length === 0 ? (
                <div style={{ padding: 18, color: "#64748b" }}>
                  No emails found
                </div>
              ) : (
                <table className="emails-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>Subject</th>
                      <th>Date</th>
                      <th>Snippet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.slice(0, 5).map((m, i) => (
                      <tr key={m.id || i}>
                        <td
                          style={{
                            maxWidth: 180,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {m.from}
                        </td>
                        <td style={{ maxWidth: 360 }}>{m.subject}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {m.date ? new Date(m.date).toLocaleString() : "—"}
                        </td>
                        <td className="email-snippet">{m.snippet || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* change password modal */}
      {showChangePwd && (
        <div
          className="modal-backdrop"
          onMouseDown={() => setShowChangePwd(false)}
        >
          <div
            className="modal"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3>Change Password</h3>
            <form onSubmit={handlePasswordChange} className="pwd-form">
              <input
                type="password"
                placeholder="Current password"
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="New password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
              />
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowChangePwd(false)}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={pwdLoading}
                >
                  {pwdLoading ? "Updating..." : "Change"}
                </button>
              </div>
              {pwdMsg && (
                <div className={`pwd-msg ${pwdMsg.type}`}>{pwdMsg.text}</div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
